import { Injectable } from '@angular/core';
import { Offer, Order, OrderItem } from '../stores/stores';
declare const fbq: any;

interface GtmEcommerceItem {
  item_id: string;
  item_name?: string;
  item_variant?: string;
  price: number;
  quantity: number;
}

interface GtmEcommercePayload {
  currency: string;
  value: number;
  items: GtmEcommerceItem[];
  transaction_id?: string;
  shipping_tier?: string;
}

interface GtmDataLayerEvent {
  event?: string;
  ecommerce?: GtmEcommercePayload | null;
  [key: string]: unknown;
}

declare global {
  interface Window {
    dataLayer?: GtmDataLayerEvent[];
  }
}

@Injectable({
  providedIn: 'root',
})
export class TrackingService {
  private readonly metaContentType = 'product_group';
  fbLoaded = false;
  gtmLoaded = false;
  private gtmEventQueueEnabled = false;
  private lastTrackedMetaUrl: string | null = null;
  private lastViewItemKey: string | null = null;

  initFacebook(fbCode: string) {
    if (!this.fbLoaded && typeof fbq !== 'undefined') {
      try {
        fbq('init', fbCode);
        this.fbLoaded = true;
        this.trackMetaPageViewIfUrlChanged(true);
        console.log('Facebook pixel initiated');
      } catch (error) {
        console.error('Failed to initialize Facebook pixel:', error);
      }
    }
  }

  configureGoogleTagManager(
    gtmCode: string | null | undefined,
    consentGranted: boolean,
  ) {
    const normalizedGtmCode = this.normalizeTrackingCode(gtmCode);
    if (!consentGranted || !normalizedGtmCode) {
      this.disableGoogleTagManager();
      return;
    }

    // Consent and a store-specific GTM container are both present. From this
    // point on, ecommerce events may be pushed to dataLayer even if gtm.js is
    // still loading; GTM consumes queued dataLayer entries once it starts.
    this.gtmEventQueueEnabled = true;
    this.loadGoogleTagManagerScript(normalizedGtmCode);
  }

  initGoogleTagManager(gtmCode: string) {
    this.configureGoogleTagManager(gtmCode, true);
  }

  disableGoogleTagManager() {
    this.gtmEventQueueEnabled = false;
    this.gtmLoaded = false;
  }

  private loadGoogleTagManagerScript(gtmCode: string) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      this.disableGoogleTagManager();
      return;
    }
    // Check if GTM script already exists
    if (document.querySelector(`script[src*="gtm.js?id=${gtmCode}"]`)) {
      this.gtmLoaded = true;
      return;
    }

    try {
      window.dataLayer = window.dataLayer ?? [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      });

      const firstScript = document.getElementsByTagName('script')[0];
      const gtmScript = document.createElement('script');
      gtmScript.async = true;
      gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=' + gtmCode;
      gtmScript.setAttribute('data-gtm-id', gtmCode); // Mark for identification

      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(gtmScript, firstScript);
      } else {
        document.head.appendChild(gtmScript);
      }

      this.gtmLoaded = true;
      console.log('Google Tag Manager initiated');
    } catch (error) {
      this.disableGoogleTagManager();
      console.error('Failed to initialize Google Tag Manager:', error);
    }
  }

  registerPurchase(order: Order, fallbackOrder?: Order | null) {
    const trackingOrder = this.resolveTrackingOrder(order, fallbackOrder);
    const trackingItems = this.resolveOrderItems(trackingOrder);
    const transactionId = this.resolveTransactionId(trackingOrder);

    const currency = this.resolveCurrency(trackingOrder);
    if (!currency) {
      this.logTrackingSkippedForInvalidCurrency('purchase', trackingOrder);
      return;
    }
    if (this.fbLoaded) {
      this.ensureMetaPageViewForCurrentUrl();
      const purchasePayload = {
        content_ids: trackingItems.map((item) => String(item.offerId)),
        content_type: this.metaContentType,
        currency,
        value: this.resolveOrderValue(trackingOrder),
        order_id: transactionId,
        delivery_category: trackingOrder.deliveryMethod,
        num_items: trackingItems.reduce((acc, val) => acc + val.quantity, 0),
        contents: trackingItems.map((item) => this.itemContent(item)),
      };
      if (transactionId) {
        fbq('track', 'Purchase', purchasePayload, {
          eventID: `purchase-${transactionId}`,
        });
      } else {
        fbq('track', 'Purchase', purchasePayload);
      }
    }

    if (this.canQueueGtmEvents()) {
      this.registerGtmPurchase(
        trackingOrder,
        currency,
        trackingItems,
        transactionId,
      );
    }
  }

  registerAddToCart(
    order: Order,
    orderItem: OrderItem,
    quantityToTrack?: number,
  ) {
    const currency = this.resolveCurrency(order);
    if (!currency) {
      this.logTrackingSkippedForInvalidCurrency('add_to_cart', order);
      return;
    }
    const trackedQuantity =
      quantityToTrack && quantityToTrack > 0
        ? quantityToTrack
        : this.resolveQuantity(orderItem);
    const trackedOrderItem = {
      ...orderItem,
      quantity: trackedQuantity,
    };
    const trackedItemPrice = this.resolveItemPrice(
      trackedOrderItem,
      trackedQuantity,
    );
    const trackedValue = trackedItemPrice * trackedQuantity;
    if (this.fbLoaded) {
      this.ensureMetaPageViewForCurrentUrl();
      fbq('track', 'AddToCart', {
        content_ids: [String(trackedOrderItem.offerId)],
        content_name: trackedOrderItem.itemName ?? trackedOrderItem.name,
        content_type: this.metaContentType,
        content_category: 'product',
        currency,
        value: trackedValue,
        num_items: trackedQuantity,
        contents: [this.itemContent(trackedOrderItem)],
      });
    }

    if (this.canQueueGtmEvents()) {
      this.registerGtmAddToCart(trackedOrderItem, currency);
    }
  }

  registerInitiateCheckout(order: Order) {
    const orderItems = this.resolveOrderItems(order);

    const currency = this.resolveCurrency(order);
    if (!currency) {
      this.logTrackingSkippedForInvalidCurrency('begin_checkout', order);
      return;
    }
    if (this.fbLoaded) {
      this.ensureMetaPageViewForCurrentUrl();
      fbq('track', 'InitiateCheckout', {
        content_category: 'product',
        content_type: this.metaContentType,
        content_ids: orderItems.map((item) => item.offerId),
        currency,
        value: this.resolveOrderValue(order),
        num_items: orderItems.reduce((acc, val) => acc + val.quantity, 0),
        contents: orderItems.map((item) => this.itemContent(item)),
      });
    }

    if (this.canQueueGtmEvents()) {
      this.registerGtmBeginCheckout(order, currency, orderItems);
    }
  }

  registerViewItem(
    offer: Offer,
    currency?: string | null,
    cartItemUuid?: string | null,
  ) {
    if (!this.canQueueGtmEvents()) {
      return;
    }
    const normalizedCurrency = this.normalizeCurrencyIsoCode(currency);
    if (!normalizedCurrency) {
      this.logTrackingSkippedForInvalidCurrency('view_item');
      return;
    }

    // Dedupe consecutive view_item pushes for the same offer / cart-item context.
    // LocationService can dispatch ViewOrderItem more than once for a single
    // intentional view (Scroll router events, cart-state refresh subscriptions).
    // Navigating to a different offer or different cart-item resets the key
    // so legitimate new views still fire exactly once.
    const viewKey = `${offer.offerId}|${cartItemUuid ?? ''}`;
    if (this.lastViewItemKey === viewKey) {
      return;
    }
    this.lastViewItemKey = viewKey;

    const item = this.mapOfferToGtmItem(offer);
    this.pushGtmEcommerceEvent('view_item', {
      currency: normalizedCurrency,
      value: item.price * item.quantity,
      items: [item],
    });
  }

  private itemContent(orderItem: OrderItem) {
    return {
      id: orderItem.offerId,
      quantity: orderItem.quantity,
      name: orderItem.name,
      itemName: orderItem.itemName,
      price: orderItem.price,
      offerPrice: orderItem.offerPrice,
      discountedOfferPrice: orderItem.discountedOfferPrice,
      totalNonDiscountedPrice: orderItem.totalNonDiscountedPrice,
      totalDiscountedPrice: orderItem.totalDiscountedPrice,
    };
  }

  private registerGtmAddToCart(orderItem: OrderItem, currency: string) {
    const item = this.mapToGtmItem(orderItem);
    const value = item.price * item.quantity;

    this.pushGtmEcommerceEvent('add_to_cart', {
      currency,
      value,
      items: [item],
    });
  }

  private registerGtmBeginCheckout(
    order: Order,
    currency: string,
    orderItems?: OrderItem[],
  ) {
    this.pushGtmEcommerceEvent('begin_checkout', {
      currency,
      value: this.resolveOrderValue(order),
      items: this.mapToGtmItems(orderItems ?? this.resolveOrderItems(order)),
    });
  }

  private registerGtmPurchase(
    order: Order,
    currency: string,
    orderItems?: OrderItem[],
    transactionId?: string,
  ) {
    this.pushGtmEcommerceEvent('purchase', {
      currency,
      value: this.resolveOrderValue(order),
      transaction_id: transactionId,
      shipping_tier: order.deliveryMethod,
      items: this.mapToGtmItems(orderItems ?? this.resolveOrderItems(order)),
    });
  }

  // GTM is designed to consume dataLayer events pushed before gtm.js loads.
  // We only queue ecommerce events after store GTM config exists and consent is granted.
  private pushGtmEcommerceEvent(
    eventName: string,
    ecommerce: GtmEcommercePayload,
  ) {
    if (!this.canQueueGtmEvents() || typeof window === 'undefined') {
      return;
    }

    window.dataLayer = window.dataLayer ?? [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: eventName,
      ecommerce,
    });
  }

  private mapToGtmItems(orderItems?: OrderItem[]): GtmEcommerceItem[] {
    return (orderItems ?? []).map((item) => this.mapToGtmItem(item));
  }

  private mapToGtmItem(orderItem: OrderItem): GtmEcommerceItem {
    const quantity = this.resolveQuantity(orderItem);

    return {
      item_id: String(orderItem.offerId),
      item_name: orderItem.itemName ?? orderItem.name,
      item_variant: orderItem.priceDescription,
      price: this.resolveItemPrice(orderItem, quantity),
      quantity,
    };
  }

  private mapOfferToGtmItem(offer: Offer): GtmEcommerceItem {
    return {
      item_id: String(offer.offerId),
      item_name: offer.name,
      item_variant: offer.priceDescription,
      price: offer.price ?? 0,
      quantity: 1,
    };
  }

  private canQueueGtmEvents(): boolean {
    return this.gtmEventQueueEnabled;
  }
  private resolveCurrency(order: Order): string | null {
    if (!order) {
      console.warn('[TrackingService] resolveCurrency called with null order');
      return null;
    }
    return this.normalizeCurrencyIsoCode(
      order.currencyIsoCode ?? order.currency,
    );
  }

  private resolveOrderValue(order: Order): number {
    if (order.totalDiscountedPrice != null) {
      return order.totalDiscountedPrice;
    }
    return this.mapToGtmItems(this.resolveOrderItems(order)).reduce(
      (sum, item) => sum + item.price * item.quantity,
      0,
    );
  }

  private resolveTrackingOrder(
    order: Order,
    fallbackOrder?: Order | null,
  ): Order {
    const resolvedOrderItems = this.resolveOrderItems(order);
    const fallbackOrderItems = this.resolveOrderItems(fallbackOrder);

    const mergedOrder: Order = {
      ...(fallbackOrder ?? {}),
      ...order,
      uuid: order?.uuid ?? fallbackOrder?.uuid,
      orderToken: order?.orderToken ?? fallbackOrder?.orderToken,
      externalOrderId: order?.externalOrderId ?? fallbackOrder?.externalOrderId,
      currencyIsoCode: order?.currencyIsoCode ?? fallbackOrder?.currencyIsoCode,
      currency: order?.currency ?? fallbackOrder?.currency,
      totalDiscountedPrice:
        order?.totalDiscountedPrice ?? fallbackOrder?.totalDiscountedPrice,
      deliveryMethod: order?.deliveryMethod ?? fallbackOrder?.deliveryMethod,
    };

    if (resolvedOrderItems.length > 0) {
      return {
        ...mergedOrder,
        orderItems: resolvedOrderItems,
      };
    }

    return {
      ...mergedOrder,
      orderItems: fallbackOrderItems,
    };
  }

  private resolveOrderItems(order?: Order | null): OrderItem[] {
    if (!order) {
      return [];
    }

    if (Array.isArray(order.orderItems) && order.orderItems.length > 0) {
      return order.orderItems;
    }

    const orderWithItemsAlias = order as Order & { items?: OrderItem[] };
    if (
      Array.isArray(orderWithItemsAlias.items) &&
      orderWithItemsAlias.items.length > 0
    ) {
      return orderWithItemsAlias.items;
    }

    return order.orderItems ?? orderWithItemsAlias.items ?? [];
  }

  private resolveQuantity(orderItem: OrderItem): number {
    return orderItem.quantity > 0 ? orderItem.quantity : 1;
  }

  private resolveItemPrice(orderItem: OrderItem, quantity: number): number {
    if (orderItem.discountedOfferPrice != null) {
      return orderItem.discountedOfferPrice;
    }
    if (orderItem.offerPrice != null) {
      return orderItem.offerPrice;
    }
    if (orderItem.price != null) {
      return orderItem.price;
    }
    if (orderItem.totalDiscountedPrice != null && quantity > 0) {
      return orderItem.totalDiscountedPrice / quantity;
    }
    return 0;
  }

  trackMetaPageViewIfUrlChanged(force = false) {
    if (!this.fbLoaded || typeof window === 'undefined') {
      return;
    }

    const currentUrl = this.resolveCurrentMetaUrl();
    if (!force && this.lastTrackedMetaUrl === currentUrl) {
      return;
    }

    fbq('track', 'PageView');
    this.lastTrackedMetaUrl = currentUrl;
  }

  private ensureMetaPageViewForCurrentUrl() {
    this.trackMetaPageViewIfUrlChanged();
  }

  private resolveCurrentMetaUrl(): string {
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  }

  private resolveTransactionId(order: Order): string | undefined {
    return order.uuid ?? order.orderToken ?? order.externalOrderId;
  }

  private normalizeCurrencyIsoCode(currency?: string | null): string | null {
    if (!currency) {
      return null;
    }

    const normalizedCurrency = currency.trim().toUpperCase();
    return /^[A-Z]{3}$/.test(normalizedCurrency) ? normalizedCurrency : null;
  }

  private normalizeTrackingCode(code?: string | null): string | null {
    const normalizedCode = code?.trim();
    return normalizedCode ? normalizedCode : null;
  }

  private logTrackingSkippedForInvalidCurrency(
    eventName: string,
    order?: Order,
  ) {
    console.warn(
      `[TrackingService] Skipping ${eventName} event: missing/invalid currency ISO code`,
      {
        orderUuid: order?.uuid,
        currencyIsoCode: order?.currencyIsoCode,
        currency: order?.currency,
      },
    );
  }
}
